<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import { itemRef } from '@/Utils/siteAccess'
import { siteAddress } from '@/Utils/siteUrl'
import { isBroadcast } from '@/components/Sites/siteTemplates'
import { contentKind } from '@/Utils/siteContentKind'
import { broadcastScheduleConflict, broadcastConflictMessage, mainLiveItemId, serviceTimeNotice } from '@/Utils/siteBroadcastSchedule'
import { fmtDateTime, dayKey, dateFromKey, moveEventToDay, addMinutes, minutesBetween, DURATION_OPTIONS, localTimeZone } from '@/Utils/siteSchedule'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconCopy from '@/components/icon/IconCopy.vue'
import IconChevronLeft from '@/components/icon/IconChevronLeftSmall.vue'
import IconChevronRight from '@/components/icon/IconChevronRightSmall.vue'
import SiteContentModal from '@/components/Sites/SiteContentModal.vue'
import SitePlanModal from '@/components/Sites/SitePlanModal.vue'
import SiteCalendarGrid from '@/components/Sites/SiteCalendarGrid.vue'

// Content › Calendar (#20, round 5). Events are time slots pointing at a
// content item; the site's /calendar page lists them. Month / week grid with
// drag-to-reschedule (PATCH /events/:id + the item's countdown.startsAt),
// click a day to add an entry, plus the older agenda list as a third view.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const store = useStore()
const route = useRoute()
const router = useRouter()
const admin = useSiteAdmin(() => props.siteId)

const loading = ref(true)
const contentModal = ref(null)
const planModal = ref(null)
const events = ref([])
const eventsLoading = ref(false)
const eventsError = ref(null)
const pendingEventDelete = ref(null)
const showPastEvents = ref(false)

const items = computed(() => admin.content.value)
const contentById = computed(() => Object.fromEntries(items.value.map((i) => [String(i.id), i])))
const broadcast = computed(() => isBroadcast(props.site))
const calendarUrl = computed(() => `${siteAddress(props.site)}/calendar`)
const copyCalendarUrl = async () => {
	try {
		await navigator.clipboard.writeText(calendarUrl.value)
		notify({ group: 'success', text: 'Calendar link copied.' })
	} catch {
		notify({ group: 'error', text: 'Could not copy the link — select it and copy instead.' })
	}
}
// plain text instead of a link while the site address is still being set up
const readiness = useSiteHostReadiness(() => props.siteId)
const addressPending = computed(() => readiness.gated.value)
const eventTitle = (event) => event.title || event.content?.title || contentFor(event)?.title || `Content #${event.contentId}`
const eventState = (event, now = Date.now()) => {
	const start = new Date(event.start).getTime()
	const end = event.end ? new Date(event.end).getTime() : start
	if (now < start) return 'upcoming'
	return now <= end ? 'live' : 'ended'
}
const EVENT_STATE = {
	upcoming: { label: 'Upcoming', variant: 'warning' },
	live: { label: 'Live now', variant: 'success' },
	ended: { label: 'Ended', variant: 'muted' },
}
const fmtRange = (event) => {
	if (!event.end) return fmtDateTime(event.start)
	const sameDay = new Date(event.start).toDateString() === new Date(event.end).toDateString()
	const endText = sameDay ? new Date(event.end).toLocaleTimeString(undefined, { timeStyle: 'short' }) : fmtDateTime(event.end)
	return `${fmtDateTime(event.start)} – ${endText}`
}
const upcomingEvents = computed(() => events.value.filter((e) => eventState(e) !== 'ended'))
const pastEvents = computed(() => events.value.filter((e) => eventState(e) === 'ended').reverse())

// --- view / navigation -----------------------------------------------------------
const VIEWS = [
	{ id: 'month', label: 'Month' },
	{ id: 'week', label: 'Week' },
	{ id: 'list', label: 'List' },
]
const view = ref(VIEWS.some((v) => v.id === route.query.view) ? String(route.query.view) : 'month')
const cursor = ref(/^\d{4}-\d{2}-\d{2}$/.test(String(route.query.day || '')) ? dateFromKey(route.query.day) : new Date())
watch([view, cursor], () => {
	const query = { ...route.query }
	delete query.view
	delete query.day
	if (view.value !== 'month') query.view = view.value
	if (dayKey(cursor.value) !== dayKey(new Date())) query.day = dayKey(cursor.value)
	if (JSON.stringify(query) !== JSON.stringify(route.query)) router.replace({ query })
})
// "Oct 12 – 18, 2026" / "Sep 28 – Oct 4, 2026": the locale decides what to
// collapse. (Formatting the end date with day + year but no month is what
// produced "2026 (day: 18)".)
const formatDateRange = (from, to) => {
	const opts = { month: 'short', day: 'numeric', year: 'numeric' }
	try {
		const f = new Intl.DateTimeFormat(undefined, opts)
		if (typeof f.formatRange === 'function') return f.formatRange(from, to)
	} catch { /* older engines: fall back to two full dates */ }
	return `${from.toLocaleDateString(undefined, opts)} – ${to.toLocaleDateString(undefined, opts)}`
}
const periodLabel = computed(() => {
	const c = cursor.value
	if (view.value === 'week') {
		const monday = new Date(c)
		monday.setDate(c.getDate() - ((c.getDay() + 6) % 7))
		const sunday = new Date(monday)
		sunday.setDate(monday.getDate() + 6)
		return formatDateRange(monday, sunday)
	}
	return c.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
})
const shift = (dir) => {
	const c = new Date(cursor.value)
	if (view.value === 'week') c.setDate(c.getDate() + 7 * dir)
	else c.setMonth(c.getMonth() + dir, 1)
	cursor.value = c
}
const goToday = () => { cursor.value = new Date() }
const eventsInPeriod = computed(() => {
	if (view.value !== 'month') return events.value
	const m = cursor.value.getMonth()
	const y = cursor.value.getFullYear()
	return events.value.filter((e) => {
		const d = new Date(e.start)
		return d.getFullYear() === y && d.getMonth() === m
	})
})

const loadEvents = async () => {
	eventsLoading.value = true
	eventsError.value = null
	try {
		const res = await SiteAdminService.listEvents(props.siteId)
		events.value = res?.items || []
	} catch (err) {
		eventsError.value = err
	}
	eventsLoading.value = false
}
const contentFor = (event) => items.value.find((i) => String(i.id) === String(event.contentId)) || null
// Posters that did not load fall back to the type placeholder, not a broken-image icon.
const brokenPosters = ref({})
const posterFailed = (event) => { brokenPosters.value = { ...brokenPosters.value, [event.id]: contentFor(event)?.poster } }
const eventPoster = (event) => {
	const poster = contentFor(event)?.poster
	return poster && brokenPosters.value[event.id] !== poster ? poster : ''
}
const openCreate = (opts = {}) => contentModal.value?.show(null, [], opts)
const openEdit = (item, opts = {}) => contentModal.value?.show(item, admin.planIdsForRef(itemRef(item.id)), opts)
const editEvent = (event) => {
	const item = contentFor(event)
	if (item) {
		eventOpen.value = false
		openEdit(item, { focus: 'schedule' })
	}
}
const requestEventDelete = (event) => {
	pendingEventDelete.value = event
	eventOpen.value = false
	showModal('delete-site-event')
}
const confirmEventDelete = async () => {
	if (!pendingEventDelete.value) return
	try {
		await SiteAdminService.deleteEvent(props.siteId, pendingEventDelete.value.id)
		notify({ group: 'success', text: `"${eventTitle(pendingEventDelete.value)}" removed from the calendar.` })
		events.value = events.value.filter((e) => e.id !== pendingEventDelete.value.id)
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not remove this calendar entry.' })
	}
	pendingEventDelete.value = null
}

// --- event popover -----------------------------------------------------------------
const eventOpen = ref(false)
const selectedEvent = ref(null)
const selectEvent = (event) => {
	selectedEvent.value = event
	eventOpen.value = true
}

// --- drag to reschedule ------------------------------------------------------------
const movingId = ref(null)
/**
 * Keep the item's countdown in step with its entries: the site shows "Upcoming"
 * from `countdown.startsAt`, the calendar page from the events — both should
 * point at the same moment. With several entries the countdown targets the
 * soonest one still ahead, not the last one edited (Trello 5owlWIBp).
 */
const soonestStartFor = (itemId, extraStarts = []) => {
	const now = Date.now()
	return [...events.value.filter((e) => String(e.contentId) === String(itemId)).map((e) => e.start), ...extraStarts]
		.filter((start) => start && new Date(start).getTime() > now)
		.sort((a, b) => new Date(a) - new Date(b))[0] || null
}
const syncCountdown = async (event, startIso) => {
	const item = contentFor(event)
	if (!item?.countdown?.enabled) return
	try {
		const res = await SiteAdminService.updateContent(props.siteId, item.id, { countdown: { enabled: true, startsAt: soonestStartFor(item.id) || startIso } })
		const fresh = res?.item || res
		if (fresh?.id) admin.content.value = admin.content.value.map((i) => (i.id === fresh.id ? fresh : i))
	} catch (err) {
		notify({ group: 'error', text: `Calendar entry moved, but the item's countdown could not be updated (${err.message || 'unknown error'}).` })
	}
}
const moveEvent = async ({ event, key }) => {
	if (movingId.value !== null) return
	const { start, end } = moveEventToDay(event, key)
	const previous = { start: event.start, end: event.end }
	movingId.value = event.id
	// optimistic
	events.value = events.value.map((e) => (e.id === event.id ? { ...e, start, end } : e))
	try {
		const res = await SiteAdminService.updateEvent(props.siteId, event.id, { start, end })
		const fresh = res?.item || res
		if (fresh?.id) events.value = events.value.map((e) => (e.id === fresh.id ? { ...e, ...fresh } : e))
		await syncCountdown(event, start)
		notify({ group: 'success', text: `"${eventTitle(event)}" moved to ${fmtDateTime(start)}.` })
	} catch (err) {
		events.value = events.value.map((e) => (e.id === event.id ? { ...e, ...previous } : e))
		notify({ group: 'error', text: err.message || 'Could not move this calendar entry.' })
	}
	movingId.value = null
}

// --- click a day → add an entry ------------------------------------------------------
const timeZone = localTimeZone()
const dayOpen = ref(false)
const dayKeySelected = ref('')
const dayForm = ref({ contentId: '', time: '20:00', durationMinutes: 60 })
const daySaving = ref(false)
const dayError = ref(null)
const dayLabel = computed(() => (dayKeySelected.value ? dateFromKey(dayKeySelected.value).toLocaleDateString(undefined, { dateStyle: 'full' }) : ''))
const contentOptions = computed(() => [
	{ value: '', text: 'Choose a content item…' },
	...[...items.value].sort((a, b) => String(a.title).localeCompare(String(b.title))).map((i) => ({ value: String(i.id), text: `${i.title}${i.published === false ? ' (hidden)' : ''}` })),
])
const dayStartIso = computed(() => {
	if (!dayKeySelected.value || !/^\d{2}:\d{2}$/.test(dayForm.value.time)) return null
	const [h, m] = dayForm.value.time.split(':').map(Number)
	const d = dateFromKey(dayKeySelected.value)
	d.setHours(h, m, 0, 0)
	return d.toISOString()
})
const selectDay = (key) => {
	dayKeySelected.value = key
	dayForm.value = { contentId: '', time: '20:00', durationMinutes: 60 }
	dayError.value = null
	dayOpen.value = true
}
const addNewContentForDay = () => {
	const start = dayStartIso.value
	dayOpen.value = false
	openCreate({ scheduledStart: start, calendar: true, focus: 'schedule' })
}
// Broadcast: the main stream's entries drive the stage countdown alongside the
// weekly service times; say so when the new entry is not the next one.
const broadcastSchedule = computed(() => {
	if (!broadcast.value) return null
	const s = admin.settings.value || {}
	return {
		serviceTimes: Array.isArray(s.broadcastServiceTimes) ? s.broadcastServiceTimes : [],
		timeZone: s.broadcastTimezone || 'UTC',
		liveItemId: mainLiveItemId(s.broadcastLiveItemId, items.value),
	}
})
const dayNotice = computed(() => {
	const b = broadcastSchedule.value
	const item = contentById.value[dayForm.value.contentId]
	if (!b || !item || !dayStartIso.value || contentKind(item) !== 'live') return ''
	if (b.liveItemId && String(b.liveItemId) !== String(item.id)) return ''
	const otherEntryStarts = events.value.filter((e) => String(e.contentId) === String(item.id)).map((e) => e.start)
	if (item.countdown?.enabled && item.countdown.startsAt) otherEntryStarts.push(item.countdown.startsAt)
	return broadcastConflictMessage(broadcastScheduleConflict({ scheduledStart: dayStartIso.value, serviceTimes: b.serviceTimes, timeZone: b.timeZone, otherEntryStarts }))
})
// Every Calendar entry is listed under the stage as a service or a one-off broadcast.
const dayServiceNotice = computed(() => {
	const b = broadcastSchedule.value
	if (!b || !dayForm.value.contentId || !dayStartIso.value) return null
	return serviceTimeNotice({ start: dayStartIso.value, serviceTimes: b.serviceTimes, timeZone: b.timeZone })
})
const canAddEntry = computed(() => Boolean(dayForm.value.contentId) && Boolean(dayStartIso.value) && !daySaving.value)
const addEntry = async () => {
	if (!canAddEntry.value) return
	const item = contentById.value[dayForm.value.contentId]
	if (!item) return
	daySaving.value = true
	dayError.value = null
	const start = dayStartIso.value
	const end = addMinutes(start, dayForm.value.durationMinutes)
	try {
		const res = await SiteAdminService.createEvent(props.siteId, { contentId: item.id, title: item.title, start, end })
		const created = res?.item || res
		events.value = [...events.value, { ...created, content: created.content || { id: item.id, title: item.title, type: item.type } }]
		// the item shows "Upcoming" until its soonest entry — same rule as the content form
		try {
			const patched = await SiteAdminService.updateContent(props.siteId, item.id, { countdown: { enabled: true, startsAt: soonestStartFor(item.id, [start]) || start } })
			const fresh = patched?.item || patched
			if (fresh?.id) admin.content.value = admin.content.value.map((i) => (i.id === fresh.id ? fresh : i))
		} catch (err) {
			notify({ group: 'error', text: `Entry added, but the item's countdown could not be set (${err.message || 'unknown error'}).` })
		}
		notify({ group: 'success', text: `"${item.title}" added to the calendar for ${fmtDateTime(start)}.` })
		dayOpen.value = false
	} catch (err) {
		dayError.value = err.message || 'Could not add this calendar entry.'
	}
	daySaving.value = false
}

onMounted(async () => {
	loading.value = true
	const pickerData = Promise.all([
		store.dispatch('Streams/getStreams').catch(() => null),
		store.dispatch('Playlist/getPlaylist').catch(() => null),
	])
	await Promise.all([admin.loadContent(), admin.loadCategories(), admin.loadPlans(), admin.loadMonetization(), admin.loadSettings(), loadEvents(), pickerData])
	loading.value = false
})
const onStripeConnected = () => Promise.all([admin.loadMonetization(), admin.loadPlans()])
const onSaved = async ({ item, planIds }) => {
	await Promise.all([admin.loadContent(), loadEvents()])
	if (item?.id) admin.reportPlanMembership(await admin.applyPlanMembership(itemRef(item.id), planIds), { done: 'Content saved' })
}
const onCreatePlan = (refs) => planModal.value?.show(null, refs, { forItem: true })
const onPlanSaved = (product) => {
	admin.upsertPlan(product)
	contentModal.value?.selectPlan(product)
}
const selectedDuration = computed(() => (selectedEvent.value ? minutesBetween(selectedEvent.value.start, selectedEvent.value.end, 0) : 0))
</script>

<template>
	<div data-testid="calendar-view">
		<!-- Text column keeps ≥ 18rem, so on a phone the actions wrap under it instead of squeezing it (Trello 2mKCTkUb). -->
		<div class="mb-4 flex flex-wrap items-center gap-3" data-testid="calendar-header">
			<div class="min-w-0 flex-1 basis-72" data-testid="calendar-header-text">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Calendar</h2>
				<p class="mb-0 text-tiny text-surface-7">
					<template v-if="broadcast">Entries show under the live stage on your site's home page (the soonest one on your live stream drives its countdown) and on the calendar page at</template><template v-else>Scheduled entries shown on your site's calendar page at</template>
					{{ ' ' }}<span v-if="addressPending" class="break-all font-medium text-surface-8" :title="readiness.lockedTitle" data-testid="calendar-url">{{ calendarUrl }}</span><a v-else :href="calendarUrl" target="_blank" rel="noopener" class="break-all font-medium text-surface-8 underline hover:text-white" data-testid="calendar-url">{{ calendarUrl }}</a><button type="button" class="ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded align-middle text-surface-7 hover:bg-surface-3 hover:text-white" title="Copy the calendar link" aria-label="Copy the calendar link" data-testid="calendar-url-copy" @click="copyCalendarUrl">
						<icon-base :width="12" :height="12" :width-view-box="16" :height-view-box="16" fill="none"><icon-copy /></icon-base>
					</button>
					Drag an entry to another day to reschedule it; click a day to add one.
				</p>
			</div>
			<div class="flex shrink-0 flex-wrap items-center gap-2" data-testid="calendar-header-actions">
				<FormButton type="secondary" size="md" :is-loading="eventsLoading" @click="loadEvents">Refresh</FormButton>
				<FormButton size="md" data-testid="calendar-add" @click="selectDay(dayKey(new Date()))">Add to calendar</FormButton>
			</div>
		</div>

		<Spinner v-if="loading" text="Loading calendar…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<template v-else>
			<Alert v-if="eventsError" type="error" class="mb-4" data-testid="calendar-error">{{ eventsError.message || 'Could not load the calendar.' }}</Alert>

			<div class="mb-3 flex flex-wrap items-center gap-2">
				<div class="flex max-w-full flex-wrap items-center gap-0.5 rounded-md bg-surface-3 p-0.5" role="group" aria-label="Calendar view" data-testid="calendar-views">
					<button
						v-for="v in VIEWS"
						:key="v.id"
						type="button"
						class="h-7 whitespace-nowrap rounded px-2.5 text-tiny font-medium transition-colors"
						:class="view === v.id ? 'bg-surface-5 text-white' : 'text-surface-8 hover:bg-surface-4 hover:text-white'"
						:aria-pressed="view === v.id"
						:data-testid="`calendar-view-${v.id}`"
						@click="view = v.id"
					>
						{{ v.label }}
					</button>
				</div>
				<template v-if="view !== 'list'">
					<div class="ml-1 flex items-center gap-1">
						<!-- The small chevrons are drawn on a 24×24 grid; icon-base defaults to 16×16 and would crop them off-centre. -->
						<FormButton is-icon type="secondary" size="sm" title="Previous" data-testid="calendar-prev" @click="shift(-1)">
							<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-left /></icon-base>
						</FormButton>
						<FormButton type="secondary" size="sm" data-testid="calendar-today" @click="goToday">Today</FormButton>
						<FormButton is-icon type="secondary" size="sm" title="Next" data-testid="calendar-next" @click="shift(1)">
							<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-right /></icon-base>
						</FormButton>
					</div>
					<h3 class="mb-0 ml-1 text-sm font-semibold text-white" data-testid="calendar-period">{{ periodLabel }}</h3>
					<span v-if="view === 'month'" class="text-tiny text-surface-7">· {{ eventsInPeriod.length }} {{ eventsInPeriod.length === 1 ? 'entry' : 'entries' }}</span>
				</template>
				<span v-if="timeZone" class="ml-auto text-tiny text-surface-7">times in {{ timeZone }}</span>
			</div>

			<SiteCalendarGrid
				v-if="view !== 'list'"
				:events="events"
				:view="view"
				:cursor="cursor"
				:content-by-id="contentById"
				:moving-id="movingId"
				@select-event="selectEvent"
				@select-day="selectDay"
				@move-event="moveEvent"
			/>

			<template v-else>
				<EmptyState v-if="!events.length && !eventsLoading" title="Nothing on the calendar yet" subtitle="Schedule a content item and tick “Show on the site calendar” to list it here and on the site.">
					<template #actions>
						<FormButton class="w-64 justify-center" @click="openCreate()">Add content</FormButton>
					</template>
				</EmptyState>
				<template v-else>
					<p v-if="!upcomingEvents.length" class="mb-3 text-tiny text-surface-7">No upcoming entries.</p>
					<ul class="m-0 flex list-none flex-col gap-2 p-0" data-testid="calendar-upcoming">
						<li v-for="event in upcomingEvents" :key="event.id" class="rounded-lg bg-surface-2" :data-testid="`event-${event.id}`">
							<div class="flex items-center gap-3 px-3.5 py-3">
								<img v-if="eventPoster(event)" :src="eventPoster(event)" alt="" class="h-11 w-20 shrink-0 rounded bg-surface-3 object-cover" @error="posterFailed(event)">
								<div v-else class="grid h-11 w-20 shrink-0 place-items-center rounded bg-surface-3 text-tiny uppercase text-surface-7">{{ event.content?.type || 'event' }}</div>
								<div class="min-w-0 flex-1">
									<div class="flex flex-wrap items-center gap-2">
										<h3 class="mb-0 truncate text-sm font-medium text-white">{{ eventTitle(event) }}</h3>
										<StatusPill pill size="sm" :variant="EVENT_STATE[eventState(event)].variant">{{ EVENT_STATE[eventState(event)].label }}</StatusPill>
										<StatusPill v-if="contentFor(event) && !contentFor(event).published" pill size="sm" variant="muted">Item hidden</StatusPill>
										<StatusPill v-else-if="!contentFor(event)" pill size="sm" variant="warning">Item missing</StatusPill>
									</div>
									<p class="mb-0 truncate text-tiny text-surface-7">
										{{ fmtRange(event) }}
										<template v-if="event.content && event.content.title !== eventTitle(event)"> · {{ event.content.title }}</template>
									</p>
								</div>
								<div class="flex shrink-0 items-center gap-1">
									<FormButton is-icon type="secondary" size="sm" title="Edit schedule" :disabled="!contentFor(event)" @click="editEvent(event)">
										<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
									</FormButton>
									<FormButton is-icon type="danger" size="sm" title="Remove from calendar" @click="requestEventDelete(event)">
										<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
									</FormButton>
								</div>
							</div>
						</li>
					</ul>

					<div v-if="pastEvents.length" class="mt-4">
						<button type="button" class="text-tiny text-surface-7 underline hover:text-white" data-testid="calendar-past-toggle" @click="showPastEvents = !showPastEvents">
							{{ showPastEvents ? 'Hide' : 'Show' }} {{ pastEvents.length }} past {{ pastEvents.length === 1 ? 'entry' : 'entries' }}
						</button>
						<ul v-if="showPastEvents" class="m-0 mt-2 flex list-none flex-col gap-2 p-0" data-testid="calendar-past">
							<li v-for="event in pastEvents" :key="event.id" class="rounded-lg bg-surface-2 opacity-75" :data-testid="`event-${event.id}`">
								<div class="flex items-center gap-3 px-3.5 py-2.5">
									<div class="min-w-0 flex-1">
										<div class="flex flex-wrap items-center gap-2">
											<h3 class="mb-0 truncate text-sm font-medium text-white">{{ eventTitle(event) }}</h3>
											<StatusPill pill size="sm" variant="muted">Ended</StatusPill>
										</div>
										<p class="mb-0 truncate text-tiny text-surface-7">{{ fmtRange(event) }}<template v-if="event.content && event.content.title !== eventTitle(event)"> · {{ event.content.title }}</template></p>
									</div>
									<div class="flex shrink-0 items-center gap-1">
										<FormButton is-icon type="secondary" size="sm" title="Edit schedule" :disabled="!contentFor(event)" @click="editEvent(event)">
											<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
										</FormButton>
										<FormButton is-icon type="danger" size="sm" title="Remove from calendar" @click="requestEventDelete(event)">
											<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
										</FormButton>
									</div>
								</div>
							</li>
						</ul>
					</div>
				</template>
			</template>
		</template>

		<!-- Event details -->
		<ModalBox v-model="eventOpen" :title="selectedEvent ? eventTitle(selectedEvent) : 'Calendar entry'" size="sm">
			<div v-if="selectedEvent" class="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="event-popover">
				<div class="flex flex-wrap items-center gap-2">
					<StatusPill pill :variant="EVENT_STATE[eventState(selectedEvent)].variant">{{ EVENT_STATE[eventState(selectedEvent)].label }}</StatusPill>
					<StatusPill v-if="contentFor(selectedEvent) && !contentFor(selectedEvent).published" pill variant="muted">Item hidden</StatusPill>
					<StatusPill v-else-if="!contentFor(selectedEvent)" pill variant="warning">Item missing</StatusPill>
				</div>
				<dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-lg bg-surface-3 px-3 py-2.5 text-sm">
					<dt class="text-surface-7">When</dt><dd class="m-0 text-white">{{ fmtRange(selectedEvent) }}</dd>
					<dt v-if="selectedDuration" class="text-surface-7">Duration</dt><dd v-if="selectedDuration" class="m-0 text-white">{{ selectedDuration >= 60 ? `${Math.round((selectedDuration / 60) * 10) / 10} h` : `${selectedDuration} min` }}</dd>
					<dt class="text-surface-7">Item</dt><dd class="m-0 truncate text-white">{{ contentFor(selectedEvent)?.title || selectedEvent.content?.title || `#${selectedEvent.contentId}` }}</dd>
				</dl>
				<p class="mb-0 text-tiny text-surface-7">Drag the entry on the grid to move it to another day; the item's countdown follows.</p>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!selectedEvent || !contentFor(selectedEvent)" @click="editEvent(selectedEvent)">Edit schedule</FormButton>
					<FormButton type="danger" class="w-full justify-center sm:w-auto" @click="requestEventDelete(selectedEvent)">Remove from calendar</FormButton>
					<FormButton type="secondary" class="w-full justify-center sm:mr-auto sm:w-auto" @click="eventOpen = false">Close</FormButton>
				</footer>
			</template>
		</ModalBox>

		<!-- Add an entry on a day -->
		<ModalBox v-model="dayOpen" :title="`Add to the calendar · ${dayLabel}`" size="sm">
			<form class="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="day-popover" @submit.prevent="addEntry">
				<Alert v-if="dayError" type="error" class="!mb-0">{{ dayError }}</Alert>
				<FormSelect id="calendar-day-content" v-model="dayForm.contentId" label="Content item" :options="contentOptions" />
				<div class="grid grid-cols-2 gap-3">
					<FormInput id="calendar-day-time" v-model="dayForm.time" type="time" label="Start" input-classes="[color-scheme:dark]" />
					<FormSelect id="calendar-day-duration" v-model="dayForm.durationMinutes" label="Duration" :options="DURATION_OPTIONS" />
				</div>
				<p class="mb-0 text-tiny text-surface-7">The item shows an “Upcoming” badge with a countdown until the start<template v-if="timeZone"> (times in {{ timeZone }})</template>.</p>
				<Alert v-if="dayNotice" type="warning" class="!mb-0" data-testid="broadcast-schedule-notice">
					<span>{{ dayNotice }}</span>
				</Alert>
				<Alert v-if="dayServiceNotice" class="!mb-0" data-testid="broadcast-service-match-notice" :data-kind="dayServiceNotice.kind">
					<span v-if="dayServiceNotice.kind === 'none'">Add service times in <router-link :to="{ name: 'SiteBroadcast', params: { siteId } }" class="underline" data-testid="broadcast-service-times-link">Settings › Broadcast</router-link> so viewers know your regular schedule.</span>
					<span v-else>{{ dayServiceNotice.text }}</span>
				</Alert>
				<button type="button" class="self-start text-tiny text-surface-8 underline hover:text-white" data-testid="day-add-new-content" @click="addNewContentForDay">Or add new content for this day…</button>
			</form>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canAddEntry" :is-loading="daySaving" data-testid="day-add-entry" @click="addEntry">Add entry</FormButton>
					<FormButton type="secondary" class="w-full justify-center sm:w-auto" @click="dayOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>

		<SiteContentModal ref="contentModal" :site-id="siteId" :categories="admin.categories.value" :plans="admin.sellablePlans.value"
			:can-monetize="admin.canMonetize.value" :stripe="admin.stripe.value" :player-open-immediately="admin.playerOpenImmediately.value" :paid-badge-label="admin.premiumBadgeLabel.value"
			:broadcast="broadcastSchedule" @saved="onSaved" @create-plan="onCreatePlan" @stripe-connected="onStripeConnected" @calendar-changed="loadEvents" />
		<SitePlanModal ref="planModal" :site-id="siteId" :content="admin.content.value"
			:can-monetize="admin.canMonetize.value" :stripe="admin.stripe.value" :capabilities="admin.planCapabilities.value" @saved="onPlanSaved" @stripe-connected="onStripeConnected" />
		<ConfirmModal modal-id="delete-site-event" modal-type="danger" ok-text="Remove" cancel-text="Cancel"
			title="Remove from the calendar?" :message="`&quot;${pendingEventDelete ? eventTitle(pendingEventDelete) : ''}&quot; disappears from the site's calendar page. The content item itself stays.`"
			@modal-confirm="confirmEventDelete" />
	</div>
</template>
